import React, { useMemo, useState, useEffect } from 'react';

// Lazy load zxcvbn (~400KB) - only loads when component mounts
let zxcvbnModule = null;

/**
 * Password strength meter component using zxcvbn library.
 * Provides real-time visual feedback on password strength.
 */
const PasswordStrengthMeter = ({ password }) => {
  const [zxcvbn, setZxcvbn] = useState(null);

  // Lazy load zxcvbn on first mount
  useEffect(() => {
    if (!zxcvbnModule) {
      import('zxcvbn').then((module) => {
        zxcvbnModule = module.default;
        setZxcvbn(() => zxcvbnModule);
      });
    } else {
      setZxcvbn(() => zxcvbnModule);
    }
  }, []);

  const result = useMemo(() => {
    if (!password || !zxcvbn) return null;
    return zxcvbn(password);
  }, [password, zxcvbn]);

  // Don't render until zxcvbn is loaded and we have a password
  if (!password || !result) return null;

  const score = result.score; // 0-4 scale

  // Color and label based on score
  const getStrengthConfig = (score) => {
    switch (score) {
      case 0:
        return {
          label: 'Very weak',
          color: 'bg-red-500',
          textColor: 'text-red-600 dark:text-red-400',
          width: '20%'
        };
      case 1:
        return {
          label: 'Weak',
          color: 'bg-orange-500',
          textColor: 'text-orange-600 dark:text-orange-400',
          width: '40%'
        };
      case 2:
        return {
          label: 'Fair',
          color: 'bg-yellow-500',
          textColor: 'text-yellow-600 dark:text-yellow-400',
          width: '60%'
        };
      case 3:
        return {
          label: 'Strong',
          color: 'bg-green-500',
          textColor: 'text-green-600 dark:text-green-400',
          width: '80%'
        };
      case 4:
        return {
          label: 'Very strong',
          color: 'bg-green-600',
          textColor: 'text-green-600 dark:text-green-400',
          width: '100%'
        };
      default:
        return {
          label: '',
          color: 'bg-gray-300',
          textColor: 'text-gray-500',
          width: '0%'
        };
    }
  };

  const config = getStrengthConfig(score);

  // Get feedback suggestions from zxcvbn
  const suggestions = result.feedback?.suggestions || [];
  const warning = result.feedback?.warning;

  return (
    <div className="mt-2 space-y-1.5 animate-in fade-in duration-200">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
          <div
            className={`h-full ${config.color} transition-all duration-300 ease-out rounded-full`}
            style={{ width: config.width }}
          />
        </div>
        <span className={`text-xs font-medium ${config.textColor} min-w-[70px] text-right`}>
          {config.label}
        </span>
      </div>

      {/* Warning or suggestion */}
      {(warning || suggestions.length > 0) && score < 3 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {warning || suggestions[0]}
        </p>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
